import React, { useState, useEffect } from 'react';
import { getPollinationsBalance } from '@/services/pollinations';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { CircleDollarSign, AlertCircle, TrendingUp, History } from 'lucide-react';
import { Button } from "@/components/ui/button";

const PollinationsBalance = () => {
  const [balance, setBalance] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchBalance = async () => {
    setLoading(true);
    const { data, error } = await getPollinationsBalance();
    if (error) setError(error);
    else setBalance(data.balance);
    setLoading(false);
  };

  useEffect(() => {
    fetchBalance();
  }, []);

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      <section>
        <h1 className="text-2xl font-syne font-bold">Pollen Reservoir</h1>
        <p className="text-sonas-muted text-xs mt-1">Real-time compute credits for global inference.</p>
      </section>

      {loading ? (
        <Skeleton className="h-64 rounded-3xl bg-white/5" />
      ) : error ? (
        <div className="bg-red-500/5 border border-red-500/20 p-8 rounded-3xl text-center space-y-4">
          <AlertCircle className="h-10 w-10 text-red-500 mx-auto" />
          <h3 className="text-lg font-bold text-red-500">{error}</h3>
          <Button onClick={fetchBalance} variant="outline" className="border-red-500/20 text-red-500 hover:bg-red-500/10">
            Retry Connection
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="bg-sonas-surface border-sonas-border rounded-3xl overflow-hidden shadow-sm relative group">
            <div className="absolute inset-0 bg-sonas-accent/5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" />
            <CardHeader>
              <CardTitle className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim flex items-center gap-2">
                <CircleDollarSign size={14} className="text-sonas-accent" /> Available Balance
              </CardTitle>
            </CardHeader>
            <CardContent className="pb-8">
              <div className="flex items-baseline gap-2">
                <span className="text-6xl font-syne font-bold">
                  {balance?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-xl font-bold text-sonas-accent">Pollen</span>
              </div>
              <p className="text-xs text-sonas-muted mt-4">Equivalent to approx. {Math.floor(balance / 0.05).toLocaleString()} standard prompts.</p>
            </CardContent>
          </Card>

          <div className="space-y-4">
            <div className="bg-sonas-surface2 border border-sonas-border p-6 rounded-2xl flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Compute Health</p>
                <p className="text-sm font-bold">System Nominal</p>
              </div>
              <TrendingUp className="text-sonas-accent" size={20} />
            </div>
            <div className="bg-sonas-surface2 border border-sonas-border p-6 rounded-2xl flex items-center justify-between">
              <div className="space-y-1">
                <p className="text-[10px] font-bold uppercase tracking-widest text-sonas-dim">Top-up Required</p>
                <p className="text-sm font-bold">{balance < 10 ? 'Critically Low' : 'Adequate'}</p>
              </div>
              <History className="text-blue-500" size={20} />
            </div>
            <Button className="w-full h-12 bg-sonas-accent text-sonas-bg font-bold rounded-xl shadow-lg shadow-sonas-accent/20">
              Increase Capacity
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PollinationsBalance;